// R-CHAT M4 — shared chat-unread total for the «پیام‌ها» nav badge.
//
// Mirrors useNotifications / useConnectSummary: the Nav is mounted for the whole
// authed session, so this fetches GET /v1/chat/unread-summary once on mount, then
// keeps it fresh two ways:
//   1. a slow ~15s poll (a fallback that also catches messages that arrived while
//      the tab was backgrounded / the socket was down), and
//   2. a live bump: a lightweight socket.io connection that listens for the SAME
//      "message" event the messenger uses — every inbound message re-fetches the
//      total (debounced via a short coalescing window so a burst is one refetch).
// Anonymous → 0 + no fetch/socket. Fully best-effort: any failure yields the last
// known value (the badge just doesn't update) — it never blocks the nav.
import React from "react";

import { useAuth } from "../auth/AuthContext";
import { chatApi } from "../api/endpoints.js";
import { sessionStore } from "../auth/session-store";

export interface UseChatUnread {
  /** Total unread messages across the user's conversations (drives the nav badge). */
  totalUnread: number;
  loading: boolean;
  /** Force a re-fetch (e.g. the messenger calls this after marking a thread read). */
  refresh: () => Promise<void>;
}

const POLL_MS = 15000;

export const useChatUnread = (): UseChatUnread => {
  const { isAuthenticated } = useAuth();
  const [totalUnread, setTotalUnread] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  // Coalesce a burst of socket "message" events into a single refetch.
  const bumpTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const refresh = React.useCallback(async () => {
    if (!isAuthenticated) {
      setTotalUnread(0);
      return;
    }
    setLoading(true);
    try {
      const res = (await chatApi.unreadSummary()) as { totalUnread?: number } | null;
      setTotalUnread(typeof res?.totalUnread === "number" ? res.totalUnread : 0);
    } catch {
      // best-effort — keep the last known value (don't zero the badge on a blip).
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Initial fetch + slow poll fallback.
  React.useEffect(() => {
    if (!isAuthenticated) {
      setTotalUnread(0);
      return;
    }
    void refresh();
    const t = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(t);
  }, [isAuthenticated, refresh]);

  // Live bump: a lightweight socket that re-fetches the total on any inbound
  // "message" (the same event the messenger fans out). Created only when authed;
  // tears down on logout. socket.io auto-falls back to long-polling if WS is
  // blocked. The import is dynamic so the ~socket.io client only loads for an
  // authed user (and never on the public/marketing shell).
  React.useEffect(() => {
    if (!isAuthenticated) return;
    const token = sessionStore.get()?.accessToken;
    if (!token) return;
    let active = true;
    let socket: { off: (e: string, f: (...a: unknown[]) => void) => void; on: (e: string, f: (...a: unknown[]) => void) => void; disconnect: () => void } | null = null;
    const onMessage = () => {
      if (bumpTimer.current) clearTimeout(bumpTimer.current);
      bumpTimer.current = setTimeout(() => void refresh(), 400);
    };
    void import("socket.io-client").then(({ io }) => {
      if (!active) return;
      const s = io({ path: "/api/socket.io", auth: { token }, transports: ["websocket", "polling"] });
      socket = s as unknown as typeof socket;
      s.on("message", onMessage);
    });
    return () => {
      active = false;
      if (bumpTimer.current) clearTimeout(bumpTimer.current);
      if (socket) {
        socket.off("message", onMessage);
        socket.disconnect();
      }
    };
  }, [isAuthenticated, refresh]);

  return { totalUnread, loading, refresh };
};
